import { useEffect } from 'react'

import {
  buildParentHiddenSecondaryConsentPatch,
  buildProbandHiddenSecondaryConsentPatch,
} from '@/config/secondaryConsentDefaults'
import type { getPatientConsents } from '@/config/uiConfig'
import type {
  CanonicalOrderPayload,
  CanonicalParentInfo,
  Comparator,
  ComparatorSecondaryConsent,
} from '@/ordering/canonicalOrder'
import type { AppDispatch } from '@/store'
import { mergePatient, upsertComparator } from '@/store/slices/canonicalOrderSlice'
import type { IncidentalFindingsSelection } from '@/types/RequisitionFormTypes'
import { comparatorIncidentalPatchWhenProbandOptOut } from '@/utils/comparatorIncidentalFindings'

type Consents = ReturnType<typeof getPatientConsents>

type ConsentSlotPatch = NonNullable<ReturnType<typeof buildParentHiddenSecondaryConsentPatch>>

/**
 * When org config marks a secondary consent as not applicable (or the slot is missing),
 * the UI hides the card; this keeps canonical order aligned with policy. Incidental
 * findings additionally defaults to opt-out whenever the user has not chosen (even when
 * the card is shown), so an untouched order is complete; NY/research → false only when
 * their slot is hidden, for proband and every comparator.
 *
 * C2.1a: parent enforcement migrated from `setMother`/`setFather` (legacy slot
 * writes) to `upsertComparator` (comparator-first writes). The
 * `buildParentHiddenSecondaryConsentPatch` helper still returns a slot-shape
 * patch; we adapt it to the comparator's nested `secondaryConsent` block here
 * to avoid a wider helper-signature change.
 *
 * Enforcement covers EVERY non-proband comparator, not just biological
 * mother/father: a non-parent comparator (sibling, grandparent, other relative)
 * needs the same incidental-findings-defaults-to-opt-out normalization, or its
 * Consent section reads as incomplete ("requires action") on the Send Updates
 * screen even though it was submitted successfully.
 */
export function useEnforceNonApplicableSecondaryConsentDefaults(
  consents: Consents,
  canonical: CanonicalOrderPayload | null,
  dispatch: AppDispatch
): void {
  useEffect(() => {
    if (!canonical) return

    const pPatch = buildProbandHiddenSecondaryConsentPatch(consents, canonical.patient)
    if (pPatch) {
      dispatch(mergePatient(pPatch))
    }

    const probandIncidental = canonical.patient?.incidentalFindingsSelection ?? null
    for (const comparator of enforceableComparators(canonical)) {
      const next = applyComparatorConsentEnforcement(comparator, consents, probandIncidental)
      if (next) dispatch(upsertComparator(next))
    }
  }, [consents, canonical, dispatch])
}

/**
 * Pure, store-free equivalent of {@link useEnforceNonApplicableSecondaryConsentDefaults}.
 *
 * Returns the canonical order with the same hidden-consent defaults applied
 * that the hook dispatches at runtime (proband `mergePatient` +
 * per-comparator `upsertComparator`). Reuses the exact same patch builders and
 * per-comparator helper, so the result is guaranteed to match what the hook
 * produces in Redux.
 *
 * Used at hydrate time to capture the dirty-detection baseline from the
 * *post-enforcement* shape. Without this, the baseline records the raw `null`
 * consents while the hook immediately normalizes them to policy defaults
 * (e.g. `incidentalFindingsSelection: null → 'opt-out'`), which then reads as
 * a phantom "unsaved change" and falsely trips the unsaved-changes nav guard
 * (e.g. clicking the Inflection logo) on a freshly-loaded order.
 */
export function applyHiddenSecondaryConsentDefaults(
  canonical: CanonicalOrderPayload,
  consents: Consents
): CanonicalOrderPayload {
  let next = canonical

  const pPatch = buildProbandHiddenSecondaryConsentPatch(consents, canonical.patient)
  if (pPatch) {
    next = { ...next, patient: { ...(next.patient ?? {}), ...pPatch } }
  }

  const comparators = next.comparators
  if (Array.isArray(comparators) && comparators.length > 0) {
    const probandIncidental = next.patient?.incidentalFindingsSelection ?? null
    let changed = false
    const updatedComparators = comparators.map((c) => {
      if (!isEnforceableComparator(c)) return c
      const updated = applyComparatorConsentEnforcement(c, consents, probandIncidental)
      if (!updated) return c
      changed = true
      return updated
    })
    if (changed) {
      next = { ...next, comparators: updatedComparators }
    }
  }

  return next
}

/**
 * Comparators the consent-default enforcement applies to: every row except the
 * proband self-row. Both biological parents AND non-parent comparators
 * (siblings, grandparents, other relatives) share the proband/parent policy so
 * an untouched Consent section is "complete" everywhere it is rendered.
 */
function isEnforceableComparator(c: Comparator | null | undefined): c is Comparator {
  return c != null && c.relationship !== 'PROBAND'
}

function enforceableComparators(canonical: CanonicalOrderPayload): Comparator[] {
  const list = canonical.comparators
  if (!Array.isArray(list)) return []
  return list.filter(isEnforceableComparator)
}

/**
 * Project a `Comparator`'s nested `secondaryConsent` block into the flat
 * slot-shape `buildParentHiddenSecondaryConsentPatch` expects, run the
 * existing patch logic, then merge any returned patch back into the
 * comparator's nested shape. Returns `null` when no patch is needed.
 *
 * The underlying policy is relationship-agnostic, so this is used for every
 * enforceable comparator (parents and non-parents alike), not only the
 * biological mother/father rows.
 */
function applyComparatorHiddenSecondaryConsentDefaults(comparator: Comparator, consents: Consents): Comparator | null {
  const sc = comparator.secondaryConsent
  const slotView: Pick<
    CanonicalParentInfo,
    'includeIncidentalFindings' | 'newYorkSampleRetentionOptIn' | 'researchContactConsent'
  > = {
    includeIncidentalFindings: sc?.includeIncidentalFindings ?? null,
    newYorkSampleRetentionOptIn: sc?.newYorkSampleRetentionOptIn ?? null,
    researchContactConsent: sc?.researchContactConsent ?? null,
  }
  const patch = buildParentHiddenSecondaryConsentPatch(consents, slotView)
  if (!patch) return null
  const nextSc: ComparatorSecondaryConsent = {
    ...(sc ?? {}),
    ...sliceConsentPatchForComparator(patch),
  }
  return { ...comparator, secondaryConsent: nextSc }
}

/**
 * Full per-comparator consent enforcement applied at hydrate time:
 *   1. hidden-slot NY/research defaults ({@link applyComparatorHiddenSecondaryConsentDefaults}), then
 *   2. the GEN-3484 proband-opt-out incidental lock — when the proband opted
 *      out, the comparator is forced to `includeIncidentalFindings: false` to
 *      match the read-only opt-out the UI already displays, so the send
 *      completeness gate doesn't read an unanswered (`null`) incidental value
 *      and block updates on reload.
 *
 * Returns the updated comparator when either step changes it, or `null` when
 * the comparator already satisfies both policies (so the caller can skip the
 * dispatch / map write).
 */
function applyComparatorConsentEnforcement(
  comparator: Comparator,
  consents: Consents,
  probandIncidental: IncidentalFindingsSelection | null
): Comparator | null {
  const afterDefaults = applyComparatorHiddenSecondaryConsentDefaults(comparator, consents)
  const base = afterDefaults ?? comparator
  const optOutPatch = comparatorIncidentalPatchWhenProbandOptOut(base, probandIncidental)
  if (!optOutPatch) return afterDefaults
  return { ...base, secondaryConsent: optOutPatch.secondaryConsent }
}

/**
 * Translate the slot-shape patch returned by
 * `buildParentHiddenSecondaryConsentPatch` into the comparator's
 * `secondaryConsent` field names. Field names happen to match today, so
 * this is structurally a passthrough — kept as a function so the type
 * narrows from `Partial<Pick<CanonicalParentInfo, ...>>` to
 * `Partial<ComparatorSecondaryConsent>` without an unsafe cast.
 */
function sliceConsentPatchForComparator(patch: ConsentSlotPatch): Partial<ComparatorSecondaryConsent> {
  const out: Partial<ComparatorSecondaryConsent> = {}
  if (patch.includeIncidentalFindings !== undefined) {
    out.includeIncidentalFindings = patch.includeIncidentalFindings
  }
  if (patch.newYorkSampleRetentionOptIn !== undefined) {
    out.newYorkSampleRetentionOptIn = patch.newYorkSampleRetentionOptIn
  }
  if (patch.researchContactConsent !== undefined) {
    out.researchContactConsent = patch.researchContactConsent
  }
  return out
}
